import { useState, useEffect } from 'react';
import { Space, Button } from '@bancobarigui/barihaus';
import { Select } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { CalendarFilters as ICalendarFilters } from '../types';
import { InspectionItemStatus } from '@/services/types/inspection.types';
import { providerCompanyService } from '@/services/providerCompany.service';
import { providerUserService } from '@/services/providerUser.service';
import { inspectorUserService } from '@/services/inspectorUser.service';
import { propertyTypeService } from '@/services/propertyType.service';
import { cityService } from '@/services/city.service';
import { User } from '@/services/types/auth.types';

interface CalendarFiltersProps {
  userRole: 'bank' | 'master_provider' | 'operational_provider' | 'inspector' | null;
  user: User | null;
  filters: ICalendarFilters;
  onChange: (filters: Partial<ICalendarFilters>) => void;
}

const STATUS_OPTIONS = [
  { label: 'Agendado', value: InspectionItemStatus.Agendado },
  { label: 'Check-in Realizado', value: InspectionItemStatus.CheckinRealizado },
  { label: 'Finalizado', value: InspectionItemStatus.Finalizado },
  { label: 'Cancelado', value: InspectionItemStatus.Cancelado },
  { label: 'Frustrado', value: InspectionItemStatus.Frustrado },
  { label: 'Aguardando Geração Laudo', value: InspectionItemStatus.AguardandoGeracaoLaudo },
  { label: 'Aguardando Revisão Prestadora', value: InspectionItemStatus.AguardandoRevisaoPrestadora },
  { label: 'Laudo Negado Por Prestadora', value: InspectionItemStatus.LaudoNegadoPorPrestadora },
  { label: 'Aguardando Revisão Banco', value: InspectionItemStatus.AguardandoRevisaoBanco },
  { label: 'Laudo Negado Por Banco', value: InspectionItemStatus.LaudoNegadoPorBanco },
  { label: 'Correção Pelo Inspetor', value: InspectionItemStatus.CorrecaoPeloInspetorBanco },
  { label: 'Laudo Aceito Pelo Banco', value: InspectionItemStatus.LaudoAceitoPeloBanco },
  { label: 'Televistoria Agendada', value: InspectionItemStatus.TelevistoriaAgendada },
  { label: 'Televistoria Finalizada', value: InspectionItemStatus.TelevistoriaFinalizada },
  { label: 'Laudo Devolvido', value: InspectionItemStatus.LaudoDevolvido },
];

export function CalendarFilters({ userRole, user, filters, onChange }: CalendarFiltersProps) {
  const [masterProviders, setMasterProviders] = useState<any[]>([]);
  const [operationalProviders, setOperationalProviders] = useState<any[]>([]);
  const [inspectors, setInspectors] = useState<any[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Carregar dados iniciais
  useEffect(() => {
    if (userRole === 'bank') {
      loadMasterProviders();
    }
    loadPropertyTypes();
    loadCities();
  }, [userRole]);

  // Carregar prestadoras operacionais quando selecionar master provider
  useEffect(() => {
    if (userRole === 'bank' && filters.providerId) {
      loadOperationalProviders(filters.providerId);
    } else if (userRole === 'master_provider' && user?.providerId) {
      // MULTI-TENANCY FIX: Filtrar por providerId do usuário logado
      loadOperationalProviders(user.providerId);
    }
  }, [userRole, filters.providerId, user?.providerId]);

  // Carregar inspetores quando selecionar operational provider
  useEffect(() => {
    if (filters.operationalProviderId) {
      const selectedProvider = operationalProviders.find(p => p.id === filters.operationalProviderId);
      loadInspectors(selectedProvider?.providerId);
    } else if (userRole === 'operational_provider' && user?.providerId) {
      // MULTI-TENANCY FIX: Filtrar por providerId do usuário logado
      loadInspectors(user.providerId);
    }
  }, [filters.operationalProviderId, userRole, operationalProviders, user?.providerId]);

  const loadMasterProviders = async () => {
    try {
      setLoading(true);
      const providers = await providerCompanyService.getAllProviderCompanies();
      const filtered = providers.filter(p => (p as any).isMasterProvider === true || (p as any).isMasterProvider === undefined);
      setMasterProviders(filtered);
    } catch {
      // Ignore errors loading providers
    } finally {
      setLoading(false);
    }
  };

  const loadOperationalProviders = async (masterProviderId?: string) => {
    try {
      setLoading(true);
      const params = masterProviderId ? { providerId: masterProviderId } : undefined;
      const providers = await providerUserService.getAllProviderUsers(params);
      setOperationalProviders(providers);
    } catch {
      // Ignore errors loading operational providers
    } finally {
      setLoading(false);
    }
  };

  const loadInspectors = async (operationalProviderId?: string) => {
    try {
      setLoading(true);
      const params = operationalProviderId ? { providerId: operationalProviderId } : undefined;
      const inspectors = await inspectorUserService.getAllInspectorUsers(params);
      setInspectors(inspectors);
    } catch {
      // Ignore errors loading inspectors
    } finally {
      setLoading(false);
    }
  };

  const loadPropertyTypes = async () => {
    try {
      const types = await propertyTypeService.getAllPropertyTypes();
      setPropertyTypes(types);
    } catch {
      // Ignore errors loading property types
    }
  };

  const loadCities = async () => {
    try {
      const citiesList = await (cityService as any).getAllCities();
      setCities(citiesList);
    } catch {
      // Ignore errors loading cities
    }
  };

  const handleClearFilters = () => {
    onChange({
      providerId: undefined,
      operationalProviderId: undefined,
      inspectorId: undefined,
      statuses: undefined,
    });
  };

  const hasActiveFilters = !!(
    filters.providerId ||
    filters.operationalProviderId ||
    filters.inspectorId ||
    (filters.statuses && filters.statuses.length > 0)
  );

  return (
    <Space wrap size="middle" style={{ width: '100%', alignItems: 'center' }}>
      {/* Status */}
      <div style={{ minWidth: '200px', flex: 1 }}>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Todos os status"
          value={filters.statuses}
          onChange={(value) => onChange({ statuses: value.length > 0 ? value : undefined })}
          options={STATUS_OPTIONS}
          maxTagCount="responsive"
          allowClear
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
      </div>

      {/* Filtros para Banco */}
      {userRole === 'bank' && (
        <div style={{ minWidth: '200px', flex: 1 }}>
          <Select
            style={{ width: '100%' }}
            placeholder="Todas as prestadoras"
            value={filters.providerId}
            onChange={(value) => {
              onChange({
                providerId: value,
                operationalProviderId: undefined,
                inspectorId: undefined,
              });
              setOperationalProviders([]);
              setInspectors([]);
            }}
            allowClear
            loading={loading}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            options={masterProviders.map(p => ({
              label: p.tradeName || p.companyName || p.name || p.corporateName || p.fantasyName || p.id,
              value: p.id,
            }))}
          />
        </div>
      )}

      {/* Filtros para Banco e Master Provider */}
      {(userRole === 'bank' || userRole === 'master_provider') && (
        <div style={{ minWidth: '200px', flex: 1 }}>
          <Select
            style={{ width: '100%' }}
            placeholder="Todas as prestadoras operacionais"
            value={filters.operationalProviderId}
            onChange={(value) => {
              onChange({
                operationalProviderId: value,
                inspectorId: undefined,
              });
              setInspectors([]);
            }}
            allowClear
            loading={loading}
            disabled={userRole === 'bank' && !filters.providerId}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            options={operationalProviders.map(p => ({
              label: p.name || p.tradeName || p.companyName || p.email || p.id,
              value: p.id,
            }))}
          />
        </div>
      )}

      {/* Filtros para Banco, Master Provider e Operational Provider */}
      {userRole !== 'inspector' && (
        <div style={{ minWidth: '200px', flex: 1 }}>
          <Select
            style={{ width: '100%' }}
            placeholder="Todos os vistoriadores"
            value={filters.inspectorId}
            onChange={(value) => onChange({ inspectorId: value })}
            allowClear
            loading={loading}
            disabled={
              (userRole === 'bank' && !filters.operationalProviderId) ||
              (userRole === 'master_provider' && !filters.operationalProviderId)
            }
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            options={inspectors.map(i => ({
              label: i.name || i.fullName || i.email || i.id,
              value: i.userId || i.id,  // Usar userId se disponível, senão usar id
            }))}
          />
        </div>
      )}

      {/* Botão limpar filtros */}
      {hasActiveFilters && (
        <Button
          type="text"
          size="small"
          icon={<CloseCircleOutlined />}
          onClick={handleClearFilters}
        >
          Limpar
        </Button>
      )}
    </Space>
  );
}
